import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { apiGet, apiRequest } from '../../../utils/fetch';
import { AdSpaceMediaTypeInterface, GenericResponseInterface } from '../../../interfaces/interfaces';
import { getAdSpaceMediaTypesColumns } from './AdSpaceMediaTypesColumns';
import { AdSpaceMediaTypesUpdateDialog } from './AdSpaceMediaTypesUpdateDialog';

const AdSpaceMediaTypesAdminList: React.FC = () => {
    const [mediaTypes, setMediaTypes] = useState<AdSpaceMediaTypeInterface[]>([]);
    const [mediaType, setMediaType] = useState<AdSpaceMediaTypeInterface | null>(null);
    const [mediaTypeId, setMediaTypeId] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [mediaTypeToDelete, setMediaTypeToDelete] = useState<AdSpaceMediaTypeInterface | null>(null);
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
    const [mediaTypeToUpdate, setMediaTypeToUpdate] = useState<AdSpaceMediaTypeInterface | null>(null);
    const [updateForm, setUpdateForm] = useState<Partial<AdSpaceMediaTypeInterface>>({});
    const [updateError, setUpdateError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Fetch all media types
    const handleGetAllMediaTypes = async () => {
        setError(null);
        setMediaType(null);
        setSuccess(null);
        try {
            const result = await apiGet<GenericResponseInterface<AdSpaceMediaTypeInterface[]>>('/adSpaceMediaTypes');
            if (result.status && result.data) {
                setMediaTypes(result.data);
            } else {
                setError(result.message || 'Failed to fetch media types');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch media types');
        }
    };

    // Fetch media type by id
    const handleGetMediaTypeById = async () => {
        setError(null);
        setMediaTypes([]);
        setSuccess(null);
        if (!mediaTypeId) return setError('Please enter a Media Type ID');
        try {
            const result = await apiGet<GenericResponseInterface<AdSpaceMediaTypeInterface>>(`/adSpaceMediaTypes/${mediaTypeId}`);
            if (result.status && result.data) {
                setMediaType(result.data);
            } else {
                setError(result.message || 'Failed to fetch media type');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch media type');
        }
    };

    // Delete media type
    const handleDeleteMediaType = async () => {
        if (!mediaTypeToDelete) return;
        setError(null);
        setSuccess(null);
        try {
            const result = await apiRequest<GenericResponseInterface>(`/adSpaceMediaTypes/delete/${mediaTypeToDelete.id}`, 'DELETE');
            if (result.status) {
                setSuccess(result.message || 'Media type deleted successfully');
                setDeleteDialogOpen(false);
                setMediaTypeToDelete(null);
                handleGetAllMediaTypes();
            } else {
                setError(result.message || 'Failed to delete media type');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to delete media type');
        }
    };

    // Update media type
    const handleUpdateMediaType = async () => {
        if (!mediaTypeToUpdate || !mediaTypeToUpdate.id) return;
        setUpdateError(null);
        setSuccess(null);

        try {
            const result = await apiRequest<GenericResponseInterface<AdSpaceMediaTypeInterface>>(
                `/adSpaceMediaTypes/update/${mediaTypeToUpdate.id}`,
                'PUT',
                {
                    ...mediaTypeToUpdate,
                    ...updateForm,
                }
            );
            if (result.status) {
                setSuccess(result.message || 'Media type updated successfully');
                setUpdateDialogOpen(false);
                setMediaTypeToUpdate(null);
                setUpdateForm({});
                handleGetAllMediaTypes();
            } else {
                setUpdateError(result.message || 'Failed to update media type');
            }
        } catch (err: any) {
            setUpdateError(err.message || 'Failed to update media type');
        }
    };

    const columns = getAdSpaceMediaTypesColumns(
        setMediaTypeToDelete,
        setDeleteDialogOpen,
        setMediaTypeToUpdate,
        setUpdateDialogOpen
    );

    useEffect(() => {
        handleGetAllMediaTypes();
    }, []);

    useEffect(() => {
        if (updateDialogOpen && mediaTypeToUpdate) {
            setUpdateForm({
                ...mediaTypeToUpdate
            });
        }
    }, [updateDialogOpen, mediaTypeToUpdate]);

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button variant="contained" onClick={handleGetAllMediaTypes}>Get All Media Types</Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button variant="contained" onClick={handleGetMediaTypeById}>Get Media Type by ID</Button>
                <TextField
                    label="Media Type ID"
                    value={mediaTypeId}
                    onChange={e => setMediaTypeId(e.target.value)}
                    size="small"
                />
            </Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            <Paper sx={{ width: '100%', overflow: 'auto', mt: 2 }}>
                {mediaTypes.length > 0 && (
                    <DataGrid
                        rows={mediaTypes.map(a => ({ ...a, id: a.id ?? Math.random() }))}
                        columns={columns}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10, page: 0 } }
                        }}
                        pageSizeOptions={[10, 25, 50]}
                        autoHeight
                    />
                )}
                {mediaType && (
                    <DataGrid
                        rows={[{ ...mediaType, id: mediaType.id ?? Math.random() }]}
                        columns={columns}
                        hideFooter
                        autoHeight
                    />
                )}
            </Paper>
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this media type?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button color="error" onClick={handleDeleteMediaType}>Delete</Button>
                </DialogActions>
            </Dialog>
            
            <AdSpaceMediaTypesUpdateDialog
                open={updateDialogOpen}
                mediaTypeToUpdate={mediaTypeToUpdate}
                setMediaTypeToUpdate={setMediaTypeToUpdate}
                onClose={() => {
                    setUpdateDialogOpen(false);
                    setSuccess(null);
                }}
                onSave={handleUpdateMediaType}
                updateError={updateError}
                updateSuccess={success}
            />
        </Box>
    );
};

export default AdSpaceMediaTypesAdminList;